import { isDefined } from "@clipboard-health/util-ts";
import { useMemo } from "react";

import { CbhFeatureFlag } from "../../../FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "../../../FeatureFlags/useCbhFlag";
import { useWorkerGeoLocation } from "../../../Worker/useWorkerGeoLocation";
import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import { useGetShiftDiscoveryDefaultDateRange } from "../../utils/useGetShiftDiscoveryDefaultDateRange";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../Filters/constants";

interface UseFallbackShiftsParams {
  appliedFiltersCount: number;
  openShiftsCount: number;
}

interface UseFallbackShiftsReturn {
  shouldFetchFallback: boolean;
  shouldShowFallbackShifts: boolean;
  fallbackShiftsData: ReturnType<typeof useGetOpenShiftsForDates>;
}

/**
 * Custom hook that manages fallback shifts logic when filters are applied but return no results.
 * Fetches unfiltered shifts as a fallback to show users there are shifts available outside their current filters.
 */
export function useFallbackShifts({
  appliedFiltersCount,
  openShiftsCount,
}: UseFallbackShiftsParams): UseFallbackShiftsReturn {
  const shouldFetchFallback =
    (openShiftsCount === 0 || openShiftsCount < 15) && appliedFiltersCount > 0;

  const workerGeoLocation = useWorkerGeoLocation();
  const windowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });
  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const includeWindowedOut = windowedOutShiftsEnabled;

  const fallbackShiftsData = useGetOpenShiftsForDates({
    dates: [],
    dateRange: defaultDateRange,
    filter: {
      area: {
        latitude: workerGeoLocation?.latitude ?? 0,
        longitude: workerGeoLocation?.longitude ?? 0,
        radiusInMiles: DEFAULT_DISTANCE_IN_MILES_FILTER,
      },
      includeWindowedOutShifts: includeWindowedOut,
    },
    enabled: shouldFetchFallback && isDefined(workerGeoLocation),
  });

  const hasFallbackShifts = fallbackShiftsData.shifts && fallbackShiftsData.shifts.length > 0;
  const shouldShowFallbackShifts = useMemo(
    () => shouldFetchFallback && hasFallbackShifts && fallbackShiftsData.isSuccess,
    [shouldFetchFallback, hasFallbackShifts, fallbackShiftsData.isSuccess]
  );

  return {
    shouldFetchFallback,
    shouldShowFallbackShifts,
    fallbackShiftsData,
  };
}
